import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { tabBarTabId } from "./tabBarIds";

export interface TabBarItem {
  value: string;
  /** Already-translated label — the component is i18n-agnostic. */
  label: string;
  /** Optional per-tab icon, hidden under the `sm` breakpoint. */
  icon?: LucideIcon;
  /** Optional trailing badge rendered after the label (e.g. a count pill). */
  badge?: ReactNode;
}

interface Props {
  tabs: TabBarItem[];
  active: string;
  onChange: (value: string) => void;
  /** Accessible label for the tablist (e.g. "Analytics", "Vehicles"). */
  ariaLabel?: string;
  /**
   * `boxed` (default) — pills inside a bordered white container.
   * `plain` — the Chat list's tabs (Sprint 222): no container, the selected
   * tab filled with `brand-sidebar` (the §10.10 token for a selected segment,
   * leaving `brand-primary` to the column's one committing action).
   */
  variant?: "boxed" | "plain";
  /**
   * Optional tabs ↔ tabpanel wiring (additive; callers that route between
   * pages pass neither). With `idPrefix` each tab gets the id
   * `tabBarTabId(idPrefix, value)` for the panel's `aria-labelledby`; with
   * `panelId` each tab gets `aria-controls={panelId}`.
   */
  idPrefix?: string;
  panelId?: string;
  /**
   * WAI-ARIA tablist keyboard model (UR-7, opt-in so page-routing callers are
   * unchanged): roving tabindex — only the selected tab is in the Tab order —
   * and ←/→ (wrapping), Home, End move focus AND select (automatic activation).
   */
  keyboardNav?: boolean;
}

/**
 * Generic, presentational pill tab group — the canonical tab style for the app
 * (Sprint 121, TASK-3858). Ported verbatim from the original `AnalyticsTabBar`
 * (Sprint 117/118): `bg-brand-primary` active pills inside a bordered white
 * container that horizontally scrolls on narrow viewports.
 *
 * Overflow affordance: when the pills overflow to the right, a fade mask plus a
 * `ChevronRight` chevron signal that more tabs exist beyond the viewport; both
 * disappear once the list is scrolled to the end. Driven by a `scroll`
 * listener + `ResizeObserver` + `window resize`.
 *
 * Purely presentational: no URL logic and no i18n calls — the caller passes
 * already-translated labels, owns the active state, and handles `onChange`.
 */
export function TabBar({
  tabs,
  active,
  onChange,
  ariaLabel,
  variant = "boxed",
  idPrefix,
  panelId,
  keyboardNav = false,
}: Props) {
  const plain = variant === "plain";
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function handleKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    const last = tabs.length - 1;
    let target: number;
    switch (event.key) {
      case "ArrowRight":
        target = index === last ? 0 : index + 1;
        break;
      case "ArrowLeft":
        target = index === 0 ? last : index - 1;
        break;
      case "Home":
        target = 0;
        break;
      case "End":
        target = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    tabRefs.current[target]?.focus();
    onChange(tabs[target].value);
  }
  // Whether there is more content to the right of the current scroll position.
  const [overflowRight, setOverflowRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      // 1px tolerance for sub-pixel rounding at the scroll end.
      setOverflowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(el);
    window.addEventListener("resize", update);

    return () => {
      el.removeEventListener("scroll", update);
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div className="relative">
      <div ref={scrollRef} className="overflow-x-auto">
        <div
          role="tablist"
          aria-label={ariaLabel}
          className={
            plain
              ? "flex w-fit items-center"
              : "flex w-fit items-center gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm"
          }
        >
          {tabs.map(({ value, label, icon: Icon, badge }, index) => (
            <button
              key={value}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              tabIndex={keyboardNav ? (active === value ? 0 : -1) : undefined}
              onKeyDown={
                keyboardNav ? (event) => handleKeyDown(event, index) : undefined
              }
              id={idPrefix ? tabBarTabId(idPrefix, value) : undefined}
              aria-controls={panelId}
              onClick={() => onChange(value)}
              aria-selected={active === value}
              aria-current={active === value ? "page" : undefined}
              className={
                plain
                  ? cn(
                      "flex h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-2 text-xs font-semibold tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary",
                      active === value
                        ? "bg-brand-sidebar text-white"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
                    )
                  : cn(
                      "flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all",
                      active === value
                        ? "bg-brand-primary text-white shadow-sm"
                        : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
                    )
              }
            >
              {Icon && <Icon size={13} className="hidden sm:block" />}
              {label}
              {plain ? badge : badge && <span className="ml-1">{badge}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Right-edge overflow affordance: fade mask + chevron when more tabs
          exist beyond the visible area. Decorative, so hidden from a11y tree. */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 flex items-center rounded-r-xl pr-1.5 pl-8 transition-opacity",
          "bg-gradient-to-l from-white via-white/90 to-transparent",
          overflowRight ? "opacity-100" : "opacity-0",
        )}
      >
        <ChevronRight size={16} className="text-gray-400" />
      </div>
    </div>
  );
}
